import { createEmptyCube, forEachCell, getNeighbors } from "./cubeTopology";
import { FACES } from "./types";
class SeededRandom {
    constructor(seed) {
        this.seed = SeededRandom.stringToSeed(seed);
    }
    static stringToSeed(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash);
    }
    next() {
        this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
        return this.seed / 0x7fffffff;
    }
    nextInt(max) {
        return Math.floor(this.next() * max);
    }
}
function generateSeed() {
    return `${Math.random().toString(36).substring(2, 10)}-1`;
}
export function cubeMineSweeper(N, MINES) {
    function emptyCube() {
        return createEmptyCube(N);
    }
    // Mutates `cube` in place: places mines avoiding the safe cell, then computes adjacency counts.
    // Same seed format as the flat-board core: "<random>-<iteration>". `replay=true` keeps the iteration
    // intact so a recorded game replays identically.
    function generateBoardInPlace(cube, safe, seedStr = "", replay = false) {
        if (seedStr === "") {
            seedStr = generateSeed();
        }
        else if (!replay) {
            const parts = seedStr.split("-");
            if (parts.length < 2 || isNaN(parseInt(parts[1]))) {
                seedStr = generateSeed();
            }
            else {
                const iteration = parseInt(parts[1]) + 1;
                seedStr = `${parts[0]}-${iteration}`;
            }
        }
        const rng = new SeededRandom(seedStr.split("-")[0]);
        let placed = 0;
        while (placed < MINES) {
            const fIdx = rng.nextInt(FACES.length);
            const r = rng.nextInt(N);
            const c = rng.nextInt(N);
            const face = FACES[fIdx];
            if (face === safe.face && r === safe.r && c === safe.c)
                continue;
            if (cube[face][r][c].isMine)
                continue;
            cube[face][r][c].isMine = true;
            placed++;
        }
        forEachCell(cube, (cell, pos) => {
            if (cell.isMine)
                return;
            let count = 0;
            for (const n of getNeighbors(pos.face, pos.r, pos.c, N)) {
                if (cube[n.face][n.r][n.c].isMine)
                    count++;
            }
            cell.adjacentMines = count;
        });
        return seedStr;
    }
    // Mutates `cube` in place: flood-fills from (face, r, c). Flood crosses face edges naturally because
    // getNeighbors does. Returns count of cells revealed; 0 if start cell is already revealed/flagged.
    function revealCellInPlace(cube, pos) {
        const start = cube[pos.face][pos.r][pos.c];
        if (start.isRevealed || start.isFlagged)
            return 0;
        const stack = [pos];
        let revealed = 0;
        while (stack.length) {
            const cur = stack.pop();
            const cell = cube[cur.face][cur.r][cur.c];
            if (cell.isRevealed || cell.isFlagged)
                continue;
            cell.isRevealed = true;
            revealed++;
            if (cell.adjacentMines === 0 && !cell.isMine) {
                for (const n of getNeighbors(cur.face, cur.r, cur.c, N)) {
                    const nb = cube[n.face][n.r][n.c];
                    if (!nb.isRevealed && !nb.isFlagged)
                        stack.push(n);
                }
            }
        }
        return revealed;
    }
    function countFlaggedAround(cube, pos) {
        let count = 0;
        for (const n of getNeighbors(pos.face, pos.r, pos.c, N)) {
            if (cube[n.face][n.r][n.c].isFlagged)
                count++;
        }
        return count;
    }
    // Mutates `cube` in place: chord-reveals neighbors of (pos). If a neighbor mine is hit, also
    // reveals all mines (used by the UI to show the lose-state). Safe reveals are kept even when a
    // mine is also hit, matching the behavior of the flat core.
    function revealAroundInPlace(cube, pos) {
        let count = 0;
        let minePos;
        for (const n of getNeighbors(pos.face, pos.r, pos.c, N)) {
            const nb = cube[n.face][n.r][n.c];
            if (nb.isFlagged || nb.isRevealed)
                continue;
            if (nb.isMine) {
                if (!minePos)
                    minePos = n;
            }
            else {
                count += revealCellInPlace(cube, n);
            }
        }
        if (minePos)
            revealAllMinesInPlace(cube);
        return { count, minePos };
    }
    function revealAllMinesInPlace(cube) {
        forEachCell(cube, (cell) => {
            if (cell.isMine && !cell.isRevealed)
                cell.isRevealed = true;
        });
    }
    function toggleFlagInPlace(cube, pos) {
        const cell = cube[pos.face][pos.r][pos.c];
        if (cell.isRevealed)
            return false;
        cell.isFlagged = !cell.isFlagged;
        return true;
    }
    // Win = every non-mine cell is revealed. Cheaper to compute via running counters in the state
    // layer, but this is the simple authoritative check.
    function isWin(cube) {
        let won = true;
        forEachCell(cube, (cell) => {
            if (!cell.isMine && !cell.isRevealed)
                won = false;
        });
        return won;
    }
    return {
        N,
        mines: MINES,
        emptyCube,
        generateBoardInPlace,
        revealCellInPlace,
        countFlaggedAround,
        revealAroundInPlace,
        revealAllMinesInPlace,
        toggleFlagInPlace,
        isWin,
    };
}
// Difficulty presets. Cell counts vs flat 2D minesweeper:
//   easy   5x5x6 = 150 cells, ~15% mines → 22  (flat easy:   81, 10 mines = 12%)
//   medium 7x7x6 = 294 cells, ~16% mines → 47  (flat medium: 256, 40 mines = 16%)
//   hard   9x9x6 = 486 cells, ~20% mines → 97  (flat hard:   480, 99 mines = 21%)
// Density on cube is bumped slightly because edge/corner cells span multiple faces, which is
// visually harder to reason about.
export function easyCubeSweeper() {
    return cubeMineSweeper(5, 22);
}
export function mediumCubeSweeper() {
    return cubeMineSweeper(7, 47);
}
export function hardCubeSweeper() {
    return cubeMineSweeper(9, 97);
}
export function sweeperForDifficulty(d) {
    switch (d) {
        case "easy": return easyCubeSweeper();
        case "medium": return mediumCubeSweeper();
        case "hard": return hardCubeSweeper();
    }
}
// Endless mode: density is stepped to match the three classic tiers, mines scale with N². Above
// N=9 we just keep the hard-tier density — the increasing N already supplies the difficulty
// curve, raising density too would push expert games into unsolvable territory.
export function densityForLevel(N) {
    if (N <= 6)
        return 22 / 150; // easy:   ~14.67%
    if (N <= 8)
        return 47 / 294; // medium: ~15.99%
    return 97 / 486; // hard:   ~19.96%
}
export function minesForLevel(N) {
    return Math.round(6 * N * N * densityForLevel(N));
}
export function cubeMineSweeperForLevel(N) {
    return cubeMineSweeper(N, minesForLevel(N));
}
// Where endless mode starts.
export const ENDLESS_START_LEVEL = 7;
